import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(3),
}));

const ContentBox = styled(Box)(() => ({
  textAlign: 'center',
  maxWidth: 600,
}));

const ErrorCode = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '8rem',
  fontWeight: 700,
  color: theme.palette.success.main,
  lineHeight: 1,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '6rem',
  },
}));

const ErrorTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '2rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '1.125rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  lineHeight: 1.6,
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  flexWrap: 'wrap',
}));

const NotFoundIllustration = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& svg': {
    width: '100%',
    maxWidth: 300,
    height: 'auto',
  },
}));

export function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <Container maxWidth="md">
        <ContentBox>
          <NotFoundIllustration>
            <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Lost location pin illustration */}
              <circle cx="100" cy="80" r="60" fill="#F3F4F6" />

              {/* Location pin */}
              <path
                d="M100 40 C85 40 75 50 75 65 C75 75 80 85 90 100 L100 115 L110 100 C120 85 125 75 125 65 C125 50 115 40 100 40 Z"
                fill="#10b981"
                opacity="0.2"
              />

              {/* Question mark in pin */}
              <text
                x="100"
                y="75"
                fontFamily="Arial, sans-serif"
                fontSize="24"
                fontWeight="bold"
                textAnchor="middle"
                fill="#10b981"
              >
                ?
              </text>

              {/* Confused lines */}
              <path d="M 60 120 Q 70 110 80 120" stroke="#9CA3AF" strokeWidth="2" fill="none" />
              <path d="M 140 120 Q 130 110 120 120" stroke="#9CA3AF" strokeWidth="2" fill="none" />
              <circle cx="55" cy="125" r="2" fill="#9CA3AF" />
              <circle cx="145" cy="125" r="2" fill="#9CA3AF" />
            </svg>
          </NotFoundIllustration>

          <ErrorCode>404</ErrorCode>
          <ErrorTitle>Page Not Found</ErrorTitle>
          <ErrorMessage>
            Oops! The page you're looking for doesn't exist or has been moved.
            Don't worry, we'll help you find your way back to the action.
          </ErrorMessage>

          <ButtonGroup>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
            >
              Go Home
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/venues')}
            >
              Browse Venues
            </Button>
          </ButtonGroup>
        </ContentBox>
      </Container>
    </PageContainer>
  );
}
